/*
 * Copyright (c) 2021 CalAmp Corp. All Rights Reserved
*/
import React, {ReactNode} from 'react';
import Collapse from '@material-ui/core/Collapse';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import {makeStyles} from '@material-ui/core/styles';
import ExpandMore from '@material-ui/icons/ExpandMore';
import classnames from 'classnames';
import {dlsColor} from '../../theme/theme';
import {DLSTheme} from '../../types/types';

export interface ExpandableListItemProps {
  listItem: ReactNode;
  expandedItem: ReactNode;
  showExpandIcon?: boolean;
  itemOpen?: boolean;
  setItemOpen?: Function;
  setExpansionsOpen?: Function;
  expansionsOpen?: any[];
  listClass?: string;
  highlightSelected?: boolean;
  highlightColor?: string;
  borderColor?: string;
  itemId?: string;
  itemSelected?: boolean;
  onItemHover?: Function;
  disableRipple?: boolean;
  disableGutters?: boolean;
  // extra classes for the expanded area - its collapse wrapper and its
  // list item - e.g. a current theme caller's own expanded look ...
  expandedWrapperClass?: string;
  expandedItemClass?: string;
}

interface ExpandableListStyleProps {
  itemSelected?: boolean;
  itemOpen?: boolean;
  highlightSelected?: boolean;
  highlightColor?: string;
  borderColor?: string;
}

const useStyles = makeStyles((theme: DLSTheme) => {
  const {
    dls: {
      motion: {
        element: {
          smallExpansion: {
            close: expansionClose,
            open: expansionOpen
          }
        }
      }
    }
  } = theme;
  return {
    closed : {
      transform  : 'rotateZ(0deg)',
      transition : `transform ${expansionClose}`
    },
    open : {
      transform  : 'rotateZ(180deg)',
      transition : `transform ${expansionOpen}`
    },
    wrapperInner : {
      backgroundColor : ({itemSelected, highlightSelected}: ExpandableListStyleProps) =>
        itemSelected && highlightSelected ? dlsColor('paleBlue2') : dlsColor('grey8')
    },
    listItem : {
      backgroundColor : getItemStyle(false),
      border          : getItemStyle(),
      borderBottom    : ({borderColor, itemOpen}: {borderColor: string, itemOpen: boolean}) =>
        itemOpen ? `solid 1px ${borderColor}` : 'none',
      borderTop : ({borderColor, itemOpen}: {borderColor: string, itemOpen: boolean}) =>
        itemOpen ? `solid 1px ${borderColor}` : dlsColor('grey5')
    },
    expandedItem : {
      backgroundColor : getItemStyle(false),
      borderLeft      : getItemStyle(),
      borderRight     : getItemStyle(),
      borderBottom    : getItemStyle(),
      '&:hover'       : {
        cursor          : 'default',
        backgroundColor : getItemStyle(false)
      }
    }
  };
});

const getItemStyle = (isBorder = true) => ({
  highlightSelected,
  itemSelected,
  borderColor,
  highlightColor
}: ExpandableListStyleProps): string => {
  const isSelected = highlightSelected && itemSelected;
  return isSelected ? (isBorder ? `solid 1px ${borderColor}` : highlightColor) : (isBorder ? 'none' : 'inherit');
};

/**
 * Material UI provides a simple Collapse wrapper than can be provided
 * to a list and list item that allows it to expand as content for another
 * list item or live on its own. Here we've grouped them together as a single
 * expandable item. Should be passed the open state and the content of the
 * listItem and the expandedItem.
 *
 * Must be provided no more than 6 columns and (preferably)
 * a length that divides evenly into 12.
 *
 * ```typescript
 *
 *   import {ExpandableListItem} from
 *     '@ion/dls/src/components/lists/ExpandableListItem';
 *   ...
 *
 *   <ExpandableListItem showExpandIcon
 *     highlightSelected
 *     listItem={<LastName />}
 *     expandedItem={<Relatives />}
 *     itemId='lastName'
 *     itemOpen={isItemOpen}
 *     setItemOpen={setItemOpen}
 *     itemSelected={isItemSelected} />
 *
 * ```
 *
 * @param   listItem             The always-visible list item's content.
 * @param   expandedItem         The content shown while expanded.
 * @param   showExpandIcon       Shows the expand/collapse icon when `true`.
 * @param   itemOpen             Expands the item when `true`.
 * @param   setItemOpen          Invoked with the item id and the new open
 *                               state when the item is clicked.
 * @param   setExpansionsOpen    Sets the open expansions.
 * @param   expansionsOpen       The currently open expansions.
 * @param   listClass            Extra className for the list item.
 * @param   highlightSelected    Highlights the item when it is selected.
 * @param   highlightColor       The selected item's background color.
 * @param   borderColor          The selected or open item's border color.
 * @param   itemId               The item's id, passed to its callbacks.
 * @param   itemSelected         Marks the item as selected.
 * @param   onItemHover          Invoked with the item id on mouse over, and
 *                               `null` on mouse leave.
 * @param   disableRipple        Disables the click ripple when `true`.
 * @param   disableGutters       Removes the list item's gutters when `true`.
 * @param   expandedWrapperClass Extra className for the expanded area's
 *                               collapse wrapper.
 * @param   expandedItemClass    Extra className for the expanded list item.
 * @returns                      The expandable list item.
 */
export const ExpandableListItem = ({
  listItem,
  expandedItem,
  showExpandIcon = false,
  itemId,
  itemOpen = false,
  setItemOpen,
  onItemHover,
  listClass,
  highlightSelected = false,
  highlightColor = dlsColor('paleBlue2'),
  borderColor = dlsColor('aaBlue'),
  itemSelected = false,
  disableRipple = false,
  disableGutters = false,
  expandedWrapperClass,
  expandedItemClass
}: ExpandableListItemProps) => {
  const classes  = useStyles({highlightSelected, highlightColor, itemSelected, borderColor, itemOpen});
  return (
    <div onMouseOver={function() { onItemHover?.(itemId); }}
      onMouseLeave={function() { onItemHover?.(null); }}>
      <ListItem button
        disableRipple={disableRipple}
        disableGutters={disableGutters}
        className={classnames(classes.listItem, listClass)}
        onClick={function() {
          setItemOpen?.(itemId, !itemOpen);
        }}>
        {listItem}
        {
          showExpandIcon && <ExpandMore className={itemOpen ? classes.closed : classes.open} />
        }
      </ListItem>
      <Collapse in={itemOpen} timeout='auto' unmountOnExit classes={{
        wrapperInner : classnames(classes.wrapperInner, expandedWrapperClass)
      }}>
        <List component='div' disablePadding>
          <ListItem button
            disableGutters
            disableRipple
            className={classnames(classes.expandedItem, expandedItemClass)}>
            {expandedItem}
          </ListItem>
        </List>
      </Collapse>
    </div>
  );
};

export default ExpandableListItem;
